// src/sim/controllers/registry.ts — shared controller instances (UI↔sim seam).
//
// localInputController: P0/P2/P3 shared LocalInputController. UI (joystick +
// HUD buttons) writes; render scene (GameScene) reads via decide().
//
// AI controller factory (P4 Plan 05 — replaces Plan 01 null-stubs):
//   initAiController: creates a per-entity AIController with seeded RNG sub-stream.
//   getAiController:  returns the AIController for a given entity id, or null if not init'd.
//   resetAiControllers: calls reset() on all registered controllers (round-start).
//   clearAiControllers: empties the map (test teardown / game restart).
//
// D-06 per-entity RNG derivation:
//   entityRng = Alea(hashSeed(rngSeed, `ai:${entityId}`))
//   One hunter's tiebreaker cannot shift another hunter's decisions (Pitfall 10).
//
// Source: .planning/phases/04-ai/04-05-PLAN.md Task 2;
//         04-CONTEXT.md D-06; 04-RESEARCH.md Pattern 5 L619-636.

import Alea from 'alea';
import { LocalInputController } from './local-input-controller';
import { AIController } from './ai-controller';
import { hashSeed } from '../rng';

/** Singleton LocalInputController. UI writes; GameScene reads. */
export const localInputController = new LocalInputController();

/** Per-entity AI controller map. Keyed by entityId. */
const _aiControllers = new Map<string, AIController>();

export interface InitAiControllerArgs {
  entityId: string;
  /** Root RNG seed used to derive the per-entity sub-stream (D-06). */
  rngSeed: number;
  /** BLOCKER-3: widened to include dog + adult roles for P4 Plan 10 spawn path. */
  initialRole: 'hunter' | 'hider' | 'dog' | 'adult';
}

/**
 * Create and register an AIController for the given entity.
 * Derives a per-entity seeded RNG sub-stream via:
 *   Alea(hashSeed(rngSeed, `ai:${entityId}`))
 * Returns the created controller.
 */
export function initAiController(args: InitAiControllerArgs): AIController {
  const entityRng = Alea(hashSeed(args.rngSeed, `ai:${args.entityId}`));
  const controller = new AIController({
    entityId: args.entityId,
    entityRng,
    initialRole: args.initialRole,
  });
  _aiControllers.set(args.entityId, controller);
  return controller;
}

/**
 * Returns the registered AIController for the given entity, or null if not initialised.
 * The tick orchestrator MUST tolerate null and emit ZERO_INTENT during boot window (WARNING-8).
 */
export function getAiController(entityId: string): AIController | null {
  return _aiControllers.get(entityId) ?? null;
}

/**
 * Calls reset() on all registered controllers. Use at round-started to clear
 * per-round state (checkedHidingSpotIds, cached intent, tree node state).
 */
export function resetAiControllers(): void {
  for (const ctrl of _aiControllers.values()) {
    ctrl.reset();
  }
}

/**
 * Clears the controller map entirely. Use in tests (afterEach) and game restart.
 */
export function clearAiControllers(): void {
  _aiControllers.clear();
}
